import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
  type FetchResult,
  type NextLink,
} from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError, type ErrorResponse } from '@apollo/client/link/error';
import { Observable } from '@apollo/client/utilities';
import { environment } from '@service/environment/environment';
import { getStoredToken, persistToken, refreshAccessToken } from '@service/keycloak/keycloak';
import { notifier } from '@service/notifier';
import { extractGraphqlMessages } from '@utils/apolloError';

const AUTH_HEADER = 'authorization';

function isUnauthorized(error: ErrorResponse): boolean {
  const network = error.networkError as { statusCode?: number; status?: number } | null;
  if (network?.statusCode === 401 || network?.status === 401) {
    return true;
  }
  return Boolean(
    error.graphQLErrors?.some((graphError) =>
      /unauthor/i.test(graphError.message) || graphError.extensions?.code === 'UNAUTHENTICATED',
    ),
  );
}

const authLink = setContext((_, previous) => {
  const token = getStoredToken();
  return {
    headers: {
      ...previous.headers,
      [AUTH_HEADER]: token,
    },
  };
});

const errorLink = onError((error) => {
  const { graphQLErrors, networkError, operation, forward } = error;

  if (isUnauthorized(error)) {
    return new Observable<FetchResult>((observer) => {
      refreshAccessToken()
        .then((token) => {
          persistToken(token);
          operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
            headers: {
              ...headers,
              [AUTH_HEADER]: token,
            },
          }));
          const subscriber = {
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          };
          (forward as NextLink)(operation).subscribe(subscriber);
        })
        .catch((refreshError: unknown) => {
          notifier.error('LABEL.saveError');
          observer.error(refreshError);
        });
    });
  }

  const message = extractGraphqlMessages(error)[0] || 'LABEL.saveError';

  if (!operation.getContext().silent) {
    notifier.error(message);
  }

  console.error('[apollo]', message, { graphQLErrors, networkError, operation: operation.operationName });
});

const httpLink = createHttpLink({
  uri: environment.graphqlHttp,
});

export const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'none',
    },
  },
});

export async function resetApolloStore(): Promise<void> {
  try {
    await apolloClient.resetStore();
  } catch (error) {
    console.error('Apollo cache reset failed', error);
  }
}
